// src/pages/Results.jsx
import React, { useEffect, useState, useMemo } from 'react'
import {
  Box,
  Badge,
  VStack,
  Text,
  Button,
  HStack,
  Heading,
  StackDivider,
  useToast
} from '@chakra-ui/react'
import { StarIcon } from '@chakra-ui/icons'
import { useNavigate, useOutletContext } from 'react-router-dom'
import Header from '../components/Header'
import { useResultsTools } from '../hooks/useResultsTools'
import { useLoads } from '../context/LoadsContext'


export default function Results() {
  const navigate = useNavigate()
  const toast = useToast()
  const { setLoads } = useLoads();
  const { loadRefs } = useOutletContext()
  const {
    loads,
    topSummary,
    highlightId,
    expandedId,
    loadingTop,
    fetchTop,
    toggleExpand,
    clickBookLoadTool,
    setHighlightId,
    setExpandedId,
  } = useResultsTools({ navigate, toast, loadRefs })


    // Start conversation and trigger widget
    useEffect(() => {
      const initiateCall = async () => {
        // add a delay before we check for
        await new Promise((res) => setTimeout(res, 1000));
  
        // Step 1: Wait for the widget to load
        const widget = document.querySelector('elevenlabs-convai');
        if (!widget) {
          console.error('ElevenLabs widget not found.');
          return;
        }
        const storedDriverId = localStorage.getItem('driverId');
        const storedSessionId = localStorage.getItem('sessionId');
        widget.setAttribute('dynamic-variables', JSON.stringify({ driver_id: storedDriverId, session_id: storedSessionId, context:"Don't follow any of the above steps, you are on the results page with loads data, just transfer to the other agent, use the tool transfer_to_agent" }));
      };
    
      initiateCall();
    }, []);

  // Modified: sort loads with top loads first, then by score descending
  const sortedLoads = useMemo(() => {
    return [...loads].sort((a, b) => {
      // First, prioritize loads with topInfo
      const aHasTop = Boolean(a.topInfo)
      const bHasTop = Boolean(b.topInfo)
      
      if (aHasTop && !bHasTop) return -1
      if (!aHasTop && bHasTop) return 1
      
      // Then sort by score
      const aScore = a.topInfo?.overall?.score ?? -Infinity
      const bScore = b.topInfo?.overall?.score ?? -Infinity
      return bScore - aScore
    })
  }, [loads])

  // Find the top load when top loads are fetched
  useEffect(() => {
    console.log("topSummary effect triggered", { topSummary, sortedLoadsLength: sortedLoads.length });
    console.log("First few loads in sortedLoads:", sortedLoads.slice(0, 3));
    
    if (topSummary && sortedLoads.length > 0) {
      // Debug all loads with topInfo
      const loadsWithTopInfo = sortedLoads.filter(load => load.topInfo);
      console.log("Loads with topInfo:", loadsWithTopInfo);
      
      // Find the load with the highest score
      const topLoad = sortedLoads.find(load => load.topInfo)
      console.log("Found top load:", topLoad);
      if (topLoad) {
        // Highlight the top load
        console.log("Setting highlight for top load:", topLoad.id);
        setHighlightId(topLoad.id)
        
        // Scroll to the top load
        const el = loadRefs.current[topLoad.id]
        if (el) {
          console.log("Scrolling to top load element");
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } else {
          console.log("Could not find element for top load:", topLoad.id);
        }
        
        // Clear highlight after a moment
        setTimeout(() => {
          console.log("Clearing highlight");
          setHighlightId(null)
        }, 1500)
      }
    }
  }, [topSummary, sortedLoads, setHighlightId, loadRefs])

  // poll localStorage for scroll & expand
  const [lastScrollIndex, setLastScrollIndex] = useState(null)
  const [lastfetchTopLoads, setLastfetchTopLoads] = useState(null)

  useEffect(() => {
    const interval = setInterval(async () => {
      const fetchTopLoadsStored = localStorage.getItem('fetchTopLoads')
      const stored = localStorage.getItem('scrollToLoadIndex')
      
      if (fetchTopLoadsStored) {
        console.log("fetchTopLoadsStored", fetchTopLoadsStored)
        setLastfetchTopLoads(fetchTopLoadsStored)
        localStorage.removeItem('fetchTopLoads')
        console.log("Calling fetchTop from interval");
        await fetchTop()  // Wait for fetchTop to complete
        return
      }
     
      if (!stored || stored === lastScrollIndex) return
      let idx;
      if (!/^\d+$/.test(stored)) {
        idx = sortedLoads.findIndex(load => load.id === stored)
        idx = idx + 1;
      }else {
        idx = parseInt(stored, 10)
      } 
      if (idx > 0 && idx <= sortedLoads.length) {
        const load = sortedLoads[idx - 1]
        const el = loadRefs.current[load.id]
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setHighlightId(load.id)
          setExpandedId(load.id)
          setTimeout(() => setHighlightId(null), 800)
        }
      } else {
        toast({ title: 'Invalid load index', status: 'error' })
      }
      setLastScrollIndex(stored)
      localStorage.removeItem('scrollToLoadIndex')
    }, 300)
    return () => clearInterval(interval)
  }, [sortedLoads, lastScrollIndex, loadRefs, setHighlightId, setExpandedId, toast, fetchTop])

  return (
    <>
      <Header title="Results" />

      <Box px={4} py={2}>
        <HStack justify="space-between" mb={4}>
          <Heading size="md">Available Loads</Heading>
          <Button onClick={fetchTop} isLoading={loadingTop} colorScheme="teal">
            Show Top Loads
          </Button>
        </HStack>

        {topSummary && (
          <Box mb={4} p={3} bg="gray.50" borderRadius="md">
            <Text fontWeight="semibold">Top Recommendation Summary:</Text>
            <Text fontSize="sm">{topSummary}</Text>
          </Box>
        )}

        <VStack spacing={4} divider={<StackDivider />} align="stretch">
          {sortedLoads.map((ld) => {
            const isPrimary  = ld.id === highlightId
            const isTopLoad  = Boolean(ld.topInfo) && !isPrimary
            const isExpanded = expandedId === ld.id

            return (
              <Box
                key={ld.id}
                id={`card-${ld.id}`}
                ref={el => { if (el) loadRefs.current[ld.id] = el }}
                position="relative"
                p={4}
                borderWidth={1}
                borderColor={
                  isPrimary  ? 'teal.500' :
                  isTopLoad  ? 'teal.200' :
                  'gray.200'
                }
                borderRadius="md"
                bg={
                  isExpanded  ? 'gray.50' :
                  isPrimary  ? 'teal.100' :
                  isTopLoad  ? 'teal.50' :
                  'white'
                }
                boxShadow={isPrimary ? '0 0 0 4px rgba(72,187,120,0.6)' : undefined}
                transition="all 0.3s ease"
              >
                {/* score badge */}
                {ld.topInfo && (
                  <Badge
                    position="absolute"
                    top="8px"
                    right="8px"
                    colorScheme="yellow"
                  >
                    {ld.topInfo.overall.score}
                  </Badge>
                )}

                {/* carrier + star */}
                <HStack align="center" mb={ld.topInfo ? 2 : 0}>
                  {ld.topInfo && (
                    <StarIcon
                      boxSize={5}
                      color={isPrimary ? 'teal.600' : 'teal.400'}
                    />
                  )}
                  <Text
                    fontWeight="semibold"
                    onClick={() => toggleExpand(ld.id)}
                    cursor="pointer"
                  >
                    {ld.carrier}
                  </Text>
                </HStack>

                {/* top-load summary inside its own card */}
                {ld.topInfo && (
                  <Text fontSize="sm" color="gray.700" mb={2}>
                    {ld.topInfo.overall_summary}
                  </Text>
                )}

                {/* route & rate */}
                <Text
                  fontSize="sm"
                  onClick={() => toggleExpand(ld.id)}
                  cursor="pointer"
                >
                  {ld.origin} → {ld.destination} • {ld.tripMiles} mi • {ld.equipment}
                </Text>
                <Text
                  fontSize="sm"
                  onClick={() => toggleExpand(ld.id)}
                  cursor="pointer"
                >
                  Rate: ${(ld.rateUsd ?? ld.estimatedRatePerMile).toFixed(2)}/mi
                </Text>

                {/* recommendation */}
                {ld.topInfo && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    {ld.topInfo.recommendation}
                  </Text>
                )}

                {/* expanded details */}
                {isExpanded && (
                  <Box mt={4} fontSize="sm">
                    <Text><strong>Deadhead:</strong> {ld.originDeadheadMiles} mi → {ld.destinationDeadheadMiles} mi</Text>
                    <Text><strong>Pickup Window:</strong> {new Date(ld.pickupEarliest).toLocaleString()} – {new Date(ld.pickupLatest).toLocaleString()}</Text>
                    <Text><strong>Expires:</strong> {new Date(ld.postingExpires).toLocaleString()}</Text>
                    <Text><strong>Weight/Length:</strong> {ld.maxWeightLbs} lbs / {ld.maxLengthFt} ft</Text>
                    <Text><strong>Bookable:</strong> {ld.isBookable ? 'Yes' : 'No'} • <strong>Negotiable:</strong> {ld.isNegotiable ? 'Yes' : 'No'}</Text>
                    <Text><strong>Broker:</strong> {ld.brokerCompany}</Text>
                    {ld.brokerEmail && <Text><strong>Email:</strong> {ld.brokerEmail}</Text>}
                    <Text><strong>Phone:</strong> {ld.brokerPhone}</Text>
                    {ld.comments && <Text><strong>Comments:</strong> {ld.comments}</Text>}

                    <Button
                      mt={4}
                      colorScheme="blue"
                      onClick={() => clickBookLoadTool({ id: ld.id })}
                    >
                      Book Load
                    </Button>
                  </Box>
                )}
              </Box>
            )
          })}
        </VStack>
      </Box>
    </>
  )
}